import { isObjectLike } from 'lodash';

import { ENDPOINTS } from '@/constants/api';
import { getApiClient } from '@/hooks/apiClient';

interface CompanionAuthorizeParams {
  sessionId: string;
  platform: string;
  idTokenHint?: string;
  promptLogin?: true;
}

export interface CompanionAuthorizeResponse {
  authorizeUrl: string;
}

const isCompanionAuthorizeResponse = (
  value: unknown,
): value is CompanionAuthorizeResponse =>
  isObjectLike(value) &&
  typeof (value as Record<string, unknown>).authorizeUrl === 'string';

/**
 * Authenticated handoff to the backend confidential flow.
 *
 * A top-level redirect can't carry the SPA Bearer token, so the launcher POSTs
 * this (Bearer-authenticated) request and navigates to the Auth0 authorize URL
 * the backend returns. The backend validates the token, mints state + PKCE bound
 * to the sessionId and the authenticated user, then rides the just-established
 * SSO session silently.
 *
 * `completeRedirect` proposes this origin's completion URL so a locally-run app
 * (localhost / ngrok) is sent back to itself after the callback. The backend
 * honors it only if allowlisted (empty in prod), so it's a no-op in production
 * where the origin already equals the configured completion URL.
 */
export async function requestCompanionAuthorize(
  { sessionId, platform, idTokenHint, promptLogin }: CompanionAuthorizeParams,
  token: string,
): Promise<CompanionAuthorizeResponse> {
  const apiClient = await getApiClient({ token });

  const { data } = await apiClient.post<CompanionAuthorizeResponse>(
    ENDPOINTS.companionAuthorize,
    {
      sessionId,
      platform,
      completeRedirect: `${window.location.origin}/external-auth/complete`,
      ...(idTokenHint && { idTokenHint }),
      ...(promptLogin && { promptLogin: true }),
    },
  );

  if (!isCompanionAuthorizeResponse(data)) {
    throw new Error('Malformed /auth/companion/authorize response');
  }

  return data;
}
